/**
 * Hex Math Library - HexGrid Class
 *
 * Convenient wrapper class that encapsulates grid configuration
 * and provides type-safe access to all hex math operations.
 */
import { axialToCube, cubeToAxial, hexToPixel, pixelToHex, hexRound } from './coordinate-conversion';
import { hexDistance } from './distance';
import { hexNeighbors, isValidHex } from './neighbors';
import { hexesInRange } from './range';
import { hexLine, hexLerp } from './line-of-sight';
import { hexRotate, hexRotateLeft, hexRotateRight } from './rotation';
/**
 * HexGrid class - Encapsulates hex grid configuration and operations
 *
 * Example usage:
 * ```typescript
 * const grid = new HexGrid({ width: 12, height: 8, hexSize: 32, orientation: 'flat-top' });
 * const neighbors = grid.neighbors({ q: 5, r: 4 });
 * const distance = grid.distance({ q: 0, r: 0 }, { q: 5, r: 3 });
 * const pixel = grid.toPixel({ q: 3, r: 2 });
 * ```
 */
export class HexGrid {
    config;
    validHexSet = null;
    constructor(config) {
        this.config = config;
    }
    /**
     * Set a custom valid hex set (for non-rectangular grid shapes).
     * When set, isValid() checks this set instead of rectangular bounds.
     */
    setValidHexes(hexes) {
        this.validHexSet = new Set(hexes.map(h => `${h.q},${h.r}`));
    }
    /**
     * Clear the custom valid hex set, reverting to rectangular bounds.
     */
    clearValidHexes() {
        this.validHexSet = null;
    }
    /**
     * Whether a custom valid hex set is active.
     */
    hasCustomShape() {
        return this.validHexSet !== null;
    }
    // ==================== Grid Configuration ====================
    /**
     * Get grid configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Get grid width (number of columns)
     */
    get width() {
        return this.config.width;
    }
    /**
     * Get grid height (number of rows)
     */
    get height() {
        return this.config.height;
    }
    /**
     * Get hex size (distance from center to corner)
     */
    get hexSize() {
        return this.config.hexSize;
    }
    /**
     * Get hex orientation
     */
    get orientation() {
        return this.config.orientation;
    }
    // ==================== Coordinate Conversion ====================
    /**
     * Convert axial to cube coordinates
     */
    axialToCube(hex) {
        return axialToCube(hex);
    }
    /**
     * Convert cube to axial coordinates
     */
    cubeToAxial(cube) {
        return cubeToAxial(cube);
    }
    /**
     * Convert hex to pixel coordinates
     */
    toPixel(hex) {
        return hexToPixel(hex, this.config);
    }
    /**
     * Convert pixel to hex coordinates (with rounding)
     */
    fromPixel(pixel) {
        return pixelToHex(pixel, this.config);
    }
    /**
     * Round fractional hex coordinates to nearest integer hex
     */
    round(hex) {
        return hexRound(hex);
    }
    // ==================== Distance & Navigation ====================
    /**
     * Calculate Manhattan distance between two hexes
     */
    distance(a, b) {
        return hexDistance(a, b);
    }
    /**
     * Get all neighboring hexes (up to 6)
     * Filters out hexes outside grid boundaries
     */
    neighbors(hex) {
        return hexNeighbors(hex, this.config);
    }
    /**
     * Get all hexes within specified range
     * Returns hexes sorted by distance (spiral order - closest first)
     */
    inRange(center, range) {
        return hexesInRange(center, range, this.config);
    }
    // ==================== Line of Sight ====================
    /**
     * Draw a line between two hexes
     * Returns all hexes along the path (inclusive)
     */
    line(start, end) {
        return hexLine(start, end);
    }
    /**
     * Linear interpolation between two hexes
     */
    lerp(start, end, t) {
        return hexLerp(start, end, t);
    }
    // ==================== Rotation ====================
    /**
     * Rotate hex around center by 60° steps
     * Positive steps = clockwise, negative = counter-clockwise
     */
    rotate(hex, center, steps) {
        return hexRotate(hex, center, steps);
    }
    /**
     * Rotate hex 60° clockwise
     */
    rotateRight(hex, center) {
        return hexRotateRight(hex, center);
    }
    /**
     * Rotate hex 60° counter-clockwise
     */
    rotateLeft(hex, center) {
        return hexRotateLeft(hex, center);
    }
    // ==================== Validation ====================
    /**
     * Check if hex is valid.
     * Uses custom valid hex set if active, otherwise rectangular bounds.
     */
    isValid(hex) {
        if (this.validHexSet) {
            return this.validHexSet.has(`${hex.q},${hex.r}`);
        }
        return isValidHex(hex, this.config);
    }
    /**
     * Check if hex equals another hex
     */
    equals(a, b) {
        return a.q === b.q && a.r === b.r;
    }
    // ==================== Utilities ====================
    /**
     * Create a hash key for hex (useful for Set/Map storage)
     */
    hash(hex) {
        return `${hex.q},${hex.r}`;
    }
    /**
     * Parse hex from hash string
     */
    fromHash(hash) {
        const parts = hash.split(',');
        if (parts.length !== 2)
            return null;
        const part0 = parts[0];
        const part1 = parts[1];
        if (!part0 || !part1)
            return null;
        const q = parseInt(part0, 10);
        const r = parseInt(part1, 10);
        if (isNaN(q) || isNaN(r))
            return null;
        return { q, r };
    }
    /**
     * Get all hexes in the grid.
     * Returns custom valid hexes if set, otherwise all hexes in rectangular bounds.
     */
    getAllHexes() {
        if (this.validHexSet) {
            const hexes = [];
            for (const key of this.validHexSet) {
                const parsed = this.fromHash(key);
                if (parsed)
                    hexes.push(parsed);
            }
            return hexes;
        }
        const hexes = [];
        for (let q = 0; q < this.config.width; q++) {
            for (let r = 0; r < this.config.height; r++) {
                hexes.push({ q, r });
            }
        }
        return hexes;
    }
    /**
     * Create a Set of hexes for efficient lookup
     */
    createHexSet(hexes) {
        return new Set(hexes.map(h => this.hash(h)));
    }
    /**
     * Create a Map from hexes to values
     */
    createHexMap(entries) {
        return new Map(entries.map(([hex, value]) => [this.hash(hex), value]));
    }
}
//# sourceMappingURL=HexGrid.js.map